<template>
  <div class="page-editor h-full flex w-full">
    <!-- Visual Editor Canvas -->
    <div class="flex-1 flex flex-col overflow-hidden relative w-full">
      <!-- Toolbar -->
      <div class="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <!-- View Options -->
          <Button
            icon="pi pi-th-large"
            :class="{ 'text-admin-primary': showGrid }"
            size="small"
            text
            @click="toggleGrid"
            :aria-label="t('admin.editor.toggleGrid')"
            v-tooltip="t('admin.editor.toggleGrid')"
          />

          <Button
            icon="pi pi-eye"
            :class="{ 'text-admin-primary': showComponentOutlines }"
            size="small"
            text
            @click="toggleComponentOutlines"
            :aria-label="t('admin.editor.toggleOutlines')"
            v-tooltip="t('admin.editor.toggleOutlines')"
          />
        </div>
        
        <!-- Page Info -->
        <div v-if="currentPage" class="text-center">
          <h2 class="font-semibold text-gray-900">{{ currentPage.title }}</h2>
          <p class="text-sm text-gray-600">{{ t('admin.editor.layoutLabel', { type: currentPage.layoutType }) }}</p>
        </div>

        <!-- Component Actions -->
        <div class="flex items-center space-x-2">
          <!-- Structure selection info -->
          <template v-if="selectedStructure">
            <span class="text-sm text-purple-600 font-medium">
              <i class="pi pi-objects-column mr-1"></i>
              {{ t('admin.editor.selected', { name: getStructureName(selectedStructure.type) }) }}
            </span>
          </template>

          <!-- Content component selection info -->
          <template v-if="selectedComponent">
            <span class="text-sm text-blue-600">{{ t('admin.editor.selected', { name: getComponentName(selectedComponent.type) }) }}</span>
            <Button
              icon="pi pi-pencil"
              size="small"
              @click="editComponent(selectedComponent)"
              :aria-label="t('admin.editor.editComponent')"
              v-tooltip="t('admin.editor.editComponent')"
            />
            <Button
              icon="pi pi-copy"
              size="small"
              :loading="duplicateComponentMutation.isPending.value"
              @click="handleDuplicateComponent"
              :aria-label="t('admin.editor.duplicateComponent')"
              v-tooltip="t('admin.editor.duplicateComponent')"
            />
            <Button
              icon="pi pi-trash"
              size="small"
              severity="danger"
              @click="handleDeleteComponent"
              :aria-label="t('admin.editor.deleteComponent')"
              v-tooltip="t('admin.editor.deleteComponent')"
            />
          </template>
          <Button
            icon="pi pi-plus"
            size="small"
            @click="addComponent"
            :aria-label="t('admin.editor.addComponent')"
            v-tooltip="t('admin.editor.addComponent')"
          />
        </div>
      </div>
      
      <!-- Editor Canvas -->
      <div class="flex-1 bg-gray-100 overflow-auto">
        <div class="min-h-full p-4">
          <div
            class="editor-canvas bg-white shadow-sm max-w-none w-full transition-all duration-200"
            :class="{ 'show-grid': showGrid }"
          >
            <!-- Loading State -->
            <div v-if="isLoading" class="flex items-center justify-center py-32">
              <ProgressSpinner />
            </div>
            
            <!-- Page Content -->
            <div
              v-else-if="currentPage"
              class="min-h-screen"
              @click="clearSelectionOnCanvasClick"
              @mousedown="armDrag"
              @dragstart="handleDragStart"
              @dragover="handleDragOver"
              @drop="handleDrop"
              @dragend="endDrag"
            >
              <!-- Render Components with Drop Zones -->
              <template v-if="rootComponents.length > 0">
                <!-- Drop zone at the beginning -->
                <DropZone
                  :position="0"
                  @add-component="handleAddComponent"
                />

                <template v-for="(component, index) in rootComponents" :key="component.id">
                  <!-- Reorderable root item: native drag, armed from its .drag-handle -->
                  <div :data-reorder-id="component.id" data-reorder-parent="root" data-reorder-axis="y">
                  <!-- Structure Wrapper for container components (FlexSection, LayoutGrid) -->
                  <AdminStructureWrapper
                    v-if="isContainerComponent(component.componentType)"
                    :component="component"
                    :is-selected="selectedStructure?.id === component.id"
                    :is-hovered="hoveredStructure === component.id"
                    :show-outlines="showComponentOutlines"
                    @edit="editComponent"
                    @nested-edit="editComponent"
                    @add-component="handleAddComponent"
                  />

                  <!-- Content Wrapper for regular components -->
                  <AdminComponentWrapper
                    v-else
                    :component="component"
                    :is-selected="selectedComponent?.id === component.id"
                    :is-hovered="hoveredComponent === component.id"
                    :show-outlines="showComponentOutlines"
                    @edit="editComponent"
                  />
                  </div>

                  <!-- Drop zone after each component -->
                  <DropZone
                    :position="component.position + 1"
                    @add-component="handleAddComponent"
                  />
                </template>
              </template>

              <!-- Empty state with drop zone -->
              <template v-else>
                <div class="flex items-center justify-center py-32">
                  <DropZone
                    :position="0"
                    message="Start building your page by adding components"
                    @add-component="handleAddComponent"
                  />
                </div>
              </template>
            </div>
            
            <!-- Empty State -->
            <div v-else class="flex items-center justify-center py-32 text-center">
              <div>
                <i class="pi pi-file-o text-gray-300 text-6xl mb-4"></i>
                <h3 class="text-xl font-semibold text-gray-900 mb-2">Page not found</h3>
                <p class="text-gray-600">The requested page could not be loaded.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar: Component Library or Property Inspector -->
    <div
      v-if="showComponentLibrary || selectedComponent"
      class="w-80 bg-white border-l border-gray-200 flex-shrink-0"
    >
      <!-- Property Inspector (when component is selected) - Priority over component library -->
      <div v-if="selectedComponent" class="flex flex-col h-full">
        <PropertyInspector
          :component="{ ...selectedComponent.data, __component: selectedComponent.type }"
          :project-id="projectIdNumber"
          @update="handlePropertyUpdate"
          @close="clearSelection"
          @duplicate="handleDuplicateComponent"
          @delete="handleDeleteComponent"
        />

        <!-- Debug: Raw JSON Data — dev-only. Vite replaces import.meta.env.DEV with a
             literal `false` in production builds, so this block (and the raw cms.* types
             + contentData it leaks) is tree-shaken out of the shipped bundle. -->
        <div v-if="isDev" class="border-t bg-gray-50 p-3 overflow-y-auto max-h-64">
          <h4 class="text-xs font-semibold text-gray-600 mb-2">Debug: Component Data</h4>
          <pre class="text-xs text-gray-700 whitespace-pre-wrap">{{ JSON.stringify(selectedComponent, null, 2) }}</pre>
        </div>
      </div>

      <!-- Component Library (when adding components and no component selected) -->
      <ComponentLibrary
        v-else-if="showComponentLibrary"
        @close="hideComponentLibrary"
        @select="handleComponentSelect"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import { useProjectQuery } from '@/composables/useProjects'
import {
  usePageQuery,
  usePageComponentsQuery,
  useCreateComponentMutation,
  useDeleteComponentMutation,
  useDuplicateComponentMutation,
  useReorderComponentsMutation
} from '@/composables/usePages'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { useUIStore, isContainerComponent } from '@/stores/ui'
import AdminComponentWrapper from '@/components/admin/AdminComponentWrapper.vue'
import AdminStructureWrapper from '@/components/admin/AdminStructureWrapper.vue'
import DropZone from '@/components/admin/DropZone.vue'
import ComponentLibrary from '@/components/admin/ComponentLibrary.vue'
import PropertyInspector from '@/components/admin/PropertyInspector.vue'
import { loggers } from '@/utils/logger'
import { getComponentDisplayName } from '@/utils/componentDisplayName'
import { reorderIds } from '@/utils/editorHelpers'

// State is now managed by UI store

// Gates the raw-JSON debug panel — true under `npm run dev`, a build-time literal
// `false` in production (so the panel and the data it dumps are tree-shaken out).
const isDev = import.meta.env.DEV

const log = loggers.pageEditor

interface Props {
  projectId: string
  pageId: string
}

const props = defineProps<Props>()
const route = useRoute()
const { t } = useI18n()

const uiStore = useUIStore()
const confirm = useConfirm()
const toast = useToast()

// Computed properties from store
const selectedComponent = computed(() => uiStore.selectedComponent)
const selectedStructure = computed(() => uiStore.selectedStructure)
const hoveredComponent = computed(() => uiStore.hoveredComponent)
const hoveredStructure = computed(() => uiStore.hoveredStructure)
const showComponentLibrary = computed(() => uiStore.showComponentLibrary)

// Convert props to reactive computed refs
const projectIdNumber = computed(() => parseInt(props.projectId))
const pageIdNumber = computed(() => parseInt(props.pageId))

// Query hooks
const { data: currentProject } = useProjectQuery(projectIdNumber)
const { data: currentPage, isLoading: isLoadingPage } = usePageQuery(pageIdNumber)
const { data: components, isLoading: isLoadingComponents } = usePageComponentsQuery(pageIdNumber)

// Component mutations
const createComponentMutation = useCreateComponentMutation()
const deleteComponentMutation = useDeleteComponentMutation()
const duplicateComponentMutation = useDuplicateComponentMutation()
const reorderComponentsMutation = useReorderComponentsMutation()

// Computed properties
const isLoading = computed(() => isLoadingPage.value || isLoadingComponents.value)
const rootComponents = computed(() => {
  if (!components?.value) return []
  const componentsList = Array.isArray(components.value) ? components.value : []
  return componentsList.filter((c: any) => !c.parentInstanceId).sort((a: any, b: any) => a.position - b.position)
})
const showGrid = computed(() => uiStore.showGrid)
const showComponentOutlines = computed(() => uiStore.showComponentOutlines)

// Methods

const toggleGrid = () => {
  uiStore.toggleGrid()
}

const toggleComponentOutlines = () => {
  uiStore.toggleComponentOutlines()
}

// No longer needed - AdminComponentWrapper handles selection directly

const editComponent = (component: any) => {
  log('editComponent called', {
    componentId: component.id,
    componentType: component.componentType
  })

  // Component is already selected by AdminComponentWrapper
  // Hide component library when entering edit mode
  if (showComponentLibrary.value) {
    log('Hiding component library due to edit mode')
    uiStore.hideComponentLibrary()
  }

  log('Edit mode activated for component', { componentId: component.id })
}

const addComponent = () => {
  log('addComponent called from toolbar')

  // If a structure is selected, add inside it
  if (selectedStructure.value) {
    const structureComponent = rootComponents.value.find((c: any) => c.id === selectedStructure.value!.id)
    const childCount = structureComponent?.children?.length || 0
    uiStore.setInsertionPoint(childCount, selectedStructure.value.id)
  }

  uiStore.showComponentLibraryAction()
  log('Component library shown')
}

const handleAddComponent = (event: { position: number; parentId?: number }) => {
  log('Add component at position', {
    position: event.position,
    parentId: event.parentId
  })

  // Clear component selection when clicking on empty slot
  if (selectedComponent.value) {
    log('Clearing component selection due to drop zone click')
    uiStore.clearSelection()
  }

  // Show component library and set insertion point
  uiStore.setInsertionPoint(event.position, event.parentId)
  uiStore.showComponentLibraryAction()

  log('Component library shown with insertion point', {
    position: event.position,
    parentId: event.parentId
  })
}

const hideComponentLibrary = () => {
  log('Hiding component library')
  uiStore.hideComponentLibrary()
}

// Default data for new components
const getDefaultComponentData = (type: string): any => {
  const defaults: Record<string, any> = {
    'cms.banner-section': { title: 'New Banner', variant: 'hero' },
    'cms.text-section': { content: '<p>New text section</p>', style: 'flow' },
    'cms.card-section': { title: 'Card Section', Cards: [] },
    'cms.flex-section': { columns: 2, Items: [] },
    'cms.accordion-section': { title: 'FAQ', Items: [] },
    'cms.contact-form': { title: 'Contact Us' },
    'cms.image-gallery': { title: 'Gallery', images: [] },
    'text.heading': { text: 'New Heading', level: 'h2' },
    'text.paragraph': { text: 'New paragraph text' },
    'text.list': { items: [], style: 'bulleted' }
  }
  return defaults[type] || {}
}

// Handle component selection from library
const handleComponentSelect = async (component: { type: string; name: string }) => {
  log('Component selected from library', { type: component.type, name: component.name })

  const insertPoint = uiStore.insertionPoint
  if (!insertPoint) {
    log('No insertion point set, using position 0')
  }

  if (!pageIdNumber.value) {
    console.error('No page ID available')
    return
  }

  const position = insertPoint?.position ?? 0
  const parentId = insertPoint?.parentId

  log('Creating component', { pageId: pageIdNumber.value, type: component.type, position, parentId })

  try {
    await createComponentMutation.mutateAsync({
      pageId: pageIdNumber.value,
      componentType: component.type,
      contentData: getDefaultComponentData(component.type),
      position,
      parentInstanceId: parentId
    })

    log('Component created successfully')

    // Hide library and clear insertion point
    uiStore.hideComponentLibrary()
  } catch (error) {
    console.error('Failed to create component:', error)
  }
}

const clearSelection = () => {
  log('Clearing component selection from PageEditor')
  uiStore.clearSelection()
}

const clearSelectionOnCanvasClick = (event: Event) => {
  log('Canvas click detected', {
    target: event.target?.constructor.name,
    currentTarget: event.currentTarget?.constructor.name,
    targetIsCurrentTarget: event.target === event.currentTarget,
    currentSelection: selectedComponent.value ? {
      id: selectedComponent.value.id,
      type: selectedComponent.value.type
    } : null,
    currentStructure: selectedStructure.value ? {
      id: selectedStructure.value.id,
      type: selectedStructure.value.type
    } : null
  })

  // Only clear selection if clicking directly on the canvas, not on a component
  if (event.target === event.currentTarget) {
    log('🎯 Direct canvas click - clearing all selections')
    uiStore.clearAllSelections()
  } else {
    log('🚫 Canvas click on child element - keeping selection', {
      childElement: event.target?.constructor.name
    })
  }
}


// Friendly component label comes from the shared util (single source of truth;
// the local copy here had drifted and was missing ~11 newer component types).
const getComponentName = getComponentDisplayName

const getStructureName = (type: string): string => {
  const nameMap: Record<string, string> = {
    'cms.flex-section': 'Flex Layout',
    'cms.layout-grid': 'Grid Layout'
  }
  return nameMap[type] || type
}

// PropertyInspector event handlers - store changes locally, save on button click
const handlePropertyUpdate = (property: string, value: any) => {
  if (!selectedComponent.value) return

  log('Property update (local)', {
    componentId: selectedComponent.value.id,
    property,
    value
  })

  // Store change locally in UI store
  uiStore.updateComponentProperty(selectedComponent.value.id, property, value)
}

const handleDuplicateComponent = async () => {
  const source = selectedComponent.value
  if (!source || duplicateComponentMutation.isPending.value) return

  log('Component duplicate requested', { componentId: source.id })

  if (typeof source.id !== 'number') {
    console.error('Cannot duplicate component: not a saved instance', source.id)
    return
  }

  try {
    const copy = await duplicateComponentMutation.mutateAsync({
      componentId: source.id,
      pageId: pageIdNumber.value
    })
    // Select the new copy (the saved content is copied; unsaved edits stay on the original).
    uiStore.selectComponent({
      id: copy.id,
      type: copy.componentType,
      name: getComponentName(copy.componentType),
      data: copy.contentData
    })
    toast.add({
      severity: 'success',
      summary: t('admin.duplicate'),
      detail: getComponentName(copy.componentType),
      life: 2500
    })
  } catch (error) {
    console.error('Failed to duplicate component:', error)
    toast.add({
      severity: 'error',
      summary: t('admin.duplicate'),
      detail: 'Failed to duplicate the component. Please try again.',
      life: 5000
    })
  }
}

// ── Drag-to-reorder (native HTML5 DnD, no library) ──────────────────────────────
// Reorderable items carry data-reorder-id / -parent ('root' or the container id) /
// -axis. A drag is only ARMED by a mousedown inside a .drag-handle, so ordinary clicks
// and text selection never start one. Drops are limited to siblings of the same parent.
interface DragState { id: number; parent: string; el: HTMLElement }
let armed: HTMLElement | null = null
let dragging: DragState | null = null
let dropMarked: HTMLElement | null = null

const reorderItemFrom = (target: EventTarget | null): HTMLElement | null =>
  (target instanceof Element ? target.closest<HTMLElement>('[data-reorder-id]') : null)

const armDrag = (event: MouseEvent) => {
  const handle = event.target instanceof Element ? event.target.closest('.drag-handle') : null
  const item = handle ? reorderItemFrom(handle) : null
  if (!item) return
  armed = item
  item.draggable = true
  window.addEventListener('mouseup', disarmDrag, { once: true })
}

const disarmDrag = () => {
  if (armed && !dragging) armed.draggable = false
  armed = null
}

const clearDropMark = () => {
  dropMarked?.classList.remove('reorder-drop-before', 'reorder-drop-after')
  dropMarked = null
}

const handleDragStart = (event: DragEvent) => {
  const item = reorderItemFrom(event.target)
  if (!item || item !== armed) return
  dragging = { id: Number(item.dataset.reorderId), parent: item.dataset.reorderParent || '', el: item }
  item.classList.add('reorder-dragging')
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(dragging.id))
  }
}

// Nearest reorderable ancestor that is a SIBLING of the dragged item (same parent).
const siblingTargetFrom = (target: EventTarget | null): HTMLElement | null => {
  let el = reorderItemFrom(target)
  while (el && dragging && el.dataset.reorderParent !== dragging.parent) {
    el = reorderItemFrom(el.parentElement)
  }
  return el
}

const isAfter = (el: HTMLElement, event: DragEvent): boolean => {
  const rect = el.getBoundingClientRect()
  return el.dataset.reorderAxis === 'x'
    ? event.clientX > rect.left + rect.width / 2
    : event.clientY > rect.top + rect.height / 2
}

const handleDragOver = (event: DragEvent) => {
  if (!dragging) return
  const target = siblingTargetFrom(event.target)
  if (!target || target === dragging.el) {
    clearDropMark()
    return
  }
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  const after = isAfter(target, event)
  if (dropMarked !== target) clearDropMark()
  dropMarked = target
  target.classList.toggle('reorder-drop-after', after)
  target.classList.toggle('reorder-drop-before', !after)
}

// Current sibling ids (in position order) for a drag parent key.
const siblingIdsFor = (parent: string): number[] => {
  if (parent === 'root') return rootComponents.value.map((c: any) => c.id)
  const container = rootComponents.value.find((c: any) => String(c.id) === parent)
  return [...(container?.children || [])]
    .sort((a: any, b: any) => a.position - b.position)
    .map((c: any) => c.id)
}

const handleDrop = (event: DragEvent) => {
  if (!dragging) return
  const target = siblingTargetFrom(event.target)
  if (!target || target === dragging.el) return endDrag()
  event.preventDefault()

  const { id, parent } = dragging
  const orderedIds = reorderIds(siblingIdsFor(parent), id, Number(target.dataset.reorderId), isAfter(target, event))
  endDrag()
  if (!orderedIds) return

  log('Reorder components', { parent, orderedIds })
  reorderComponentsMutation.mutate(
    {
      pageId: pageIdNumber.value,
      parentInstanceId: parent === 'root' ? null : Number(parent),
      orderedIds
    },
    {
      onError: (error) => {
        console.error('Failed to reorder components:', error)
        toast.add({
          severity: 'error',
          summary: t('admin.dragToReorder'),
          detail: 'Could not save the new order. Please try again.',
          life: 5000
        })
      }
    }
  )
}

const endDrag = () => {
  clearDropMark()
  if (dragging) {
    dragging.el.classList.remove('reorder-dragging')
    dragging.el.draggable = false
  }
  if (armed) armed.draggable = false
  dragging = null
  armed = null
}

const handleDeleteComponent = () => {
  if (!selectedComponent.value) return

  log('Component delete requested', {
    componentId: selectedComponent.value.id,
    componentType: selectedComponent.value.type
  })

  const componentName = getComponentName(selectedComponent.value.type)
  const componentId = selectedComponent.value.id

  confirm.require({
    message: `Are you sure you want to delete this ${componentName}? This action cannot be undone.`,
    header: 'Delete Component',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await deleteComponentMutation.mutateAsync({
          componentId,
          pageId: pageIdNumber.value
        })

        // Clear selection after successful deletion
        uiStore.clearSelection()

        toast.add({
          severity: 'success',
          summary: 'Component Deleted',
          detail: `${componentName} has been deleted successfully.`,
          life: 3000
        })

        log('Component deleted successfully', { componentId })
      } catch (error) {
        console.error('Failed to delete component:', error)
        toast.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: 'Failed to delete the component. Please try again.',
          life: 5000
        })
      }
    },
    reject: () => {
      log('Component deletion cancelled', { componentId })
    }
  })
}


// Lifecycle
onMounted(() => {
  // Keyboard handlers are now managed by individual AdminComponentWrapper instances
  log('PageEditor mounted')
})

onUnmounted(() => {
  // Cleanup if needed
  log('PageEditor unmounted')
})

// No need to watch route changes - TanStack Query handles this automatically
// based on the pageId prop changes
</script>

<style scoped>
.page-editor {
  width: 100% !important;
  max-width: none !important;
}

.editor-canvas {
  min-height: 800px;
  width: 100% !important;
  max-width: none !important;
}

.editor-canvas.show-grid {
  background-image: 
    linear-gradient(rgba(0,0,0,.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,.05) 1px, transparent 1px);
  background-size: 20px 20px;
}

.show-grid .admin-component {
  position: relative;
}

.show-grid .admin-component::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1px dashed rgba(59, 130, 246, 0.3);
  pointer-events: none;
}
</style>

<!-- Unscoped: drag-to-reorder marks are toggled imperatively on items rendered by child
     components (AdminStructureWrapper slots), which scoped styles can't reach. -->
<style>
.reorder-dragging {
  opacity: 0.4;
}

.reorder-drop-before,
.reorder-drop-after {
  position: relative;
}

.reorder-drop-before::after,
.reorder-drop-after::after {
  content: '';
  position: absolute;
  z-index: 30;
  background: #3b82f6;
  pointer-events: none;
}

.reorder-drop-before[data-reorder-axis='y']::after { top: -2px; left: 0; right: 0; height: 4px; }
.reorder-drop-after[data-reorder-axis='y']::after { bottom: -2px; left: 0; right: 0; height: 4px; }
.reorder-drop-before[data-reorder-axis='x']::after { left: -2px; top: 0; bottom: 0; width: 4px; }
.reorder-drop-after[data-reorder-axis='x']::after { right: -2px; top: 0; bottom: 0; width: 4px; }
</style>
